import { isReferencedNodeValue } from "@doitintl/cloudflow-commons";
import { CloudFlowNodeType, type INodeModel, type NodeConfigApiParameters } from "@doitintl/cmp-models";
import { type Node, useReactFlow } from "@xyflow/react";

import { type RFNode } from "../../types";
import { useNodeConfigurationContext } from "../ConfigurationPanel/NodeConfigurationContext";

type NodeType<Type extends CloudFlowNodeType = CloudFlowNodeType> = Node<RFNode<Type>, Type>;

type GetNodeFn = (nodeId: string | undefined) => NodeType | undefined;

const filtersAndTransformationsTypes = [
  CloudFlowNodeType.TRANSFORMATION,
  CloudFlowNodeType.DATETIME_TRANSFORM,
  CloudFlowNodeType.FILTER,
  CloudFlowNodeType.CONDITION,
] as const;

function isNodeType<T extends CloudFlowNodeType>(
  nodeData: INodeModel<CloudFlowNodeType>,
  ...type: T[]
): nodeData is INodeModel<T> {
  return type.includes(nodeData.type as T);
}

function getReferencedNodeFromParams(getNode: GetNodeFn, parameters: unknown): NodeType | undefined {
  if (isReferencedNodeValue(parameters)) {
    const node = getNode(parameters.referencedNodeId);
    return node && isNodeType(node?.data.nodeData, CloudFlowNodeType.ACTION, ...filtersAndTransformationsTypes)
      ? node
      : undefined;
  }

  let nestedParameters: unknown[] = [];
  if (Array.isArray(parameters)) {
    nestedParameters = parameters;
  } else if (typeof parameters === "object" && parameters !== null) {
    nestedParameters = Object.values(parameters);
  }

  for (const value of nestedParameters) {
    const node = getReferencedNodeFromParams(getNode, value);
    if (node !== undefined) {
      return node;
    }
  }
}

function getReferencedActionNodeWithConfigurationValues<ParametersType extends NodeConfigApiParameters>(
  getNode: GetNodeFn,
  isMatchingProvider: (parameters: NodeConfigApiParameters | undefined) => parameters is ParametersType,
  hasConfigurationValues: (actionNodeParameters: ParametersType) => boolean,
  formValues: unknown
):
  | {
      stepNumber: number | undefined;
      name: string;
      configurationValues: ParametersType["configurationValues"];
    }
  | undefined {
  let node: NodeType | undefined = getReferencedNodeFromParams(getNode, formValues);
  while (node) {
    const nodeData = node.data.nodeData;
    switch (true) {
      case isNodeType(nodeData, CloudFlowNodeType.ACTION): {
        if (!isMatchingProvider(nodeData.parameters)) {
          return undefined;
        }
        if (hasConfigurationValues(nodeData.parameters)) {
          return {
            stepNumber: node.data.stepNumber,
            name: nodeData.name,
            configurationValues: nodeData.parameters?.configurationValues,
          };
        }
        node = getReferencedNodeFromParams(getNode, nodeData.parameters?.formValues);
        break;
      }
      case isNodeType(nodeData, ...filtersAndTransformationsTypes):
        node = getNode(nodeData.parameters?.referencedNodeId);
        break;
      default:
        node = undefined;
    }
  }
}

export function useInheritedConfigurationValues<ParametersType extends NodeConfigApiParameters>(
  hasConfigurationValues: (actionNodeParameters: ParametersType) => boolean
) {
  const { nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const { getNode } = useReactFlow();
  const isMatchingProvider = (parameters: NodeConfigApiParameters | undefined): parameters is ParametersType =>
    parameters?.provider === nodeConfig.parameters.provider;

  return getReferencedActionNodeWithConfigurationValues(
    (id: string | undefined) => getNode(id ?? "") as NodeType<CloudFlowNodeType.ACTION> | undefined,
    isMatchingProvider,
    hasConfigurationValues,
    nodeConfig.parameters.formValues
  );
}
